import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, Button as MuiButton, Stack } from '@mui/material';
import { useGlobalStore } from '@siloreal:app:store';
import {
  Children,
  ChangeEvent,
  forwardRef,
  ForwardRefExoticComponent,
  FocusEvent,
  isValidElement,
  KeyboardEvent,
  PropsWithChildren,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';
import { useRouteController } from '../../../hooks/controller/route/use-route-controller';
import { Button } from '../../basic/button/button';
import { Typography } from '../../basic/typography/typography';
import { Drawer } from '../../basic/drawer/drawer';
import { base as FormBase } from '../../form/base/form-base';
import { divider as FormDivider } from '../../form/divider/form-divider';
import { text as TextInput } from '../../form/field/text-input';
import { Modal } from '../../modal/modal';
import * as HOOKS from '@siloreal:shared:hooks';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

export type FilterBaseHandle = {
  read: () => FieldValues;
  count: () => number;
  hasActiveFilters: () => boolean;
  pinned: () => FilterPinnedFilter[];
  applyPinned: (id: string) => void;
  clear: () => void;
  reset: () => void;
};

export type Props = Required<PropsWithChildren> & {
  drawer?: boolean;
  open?: boolean;
  onClose?: () => void;
  onSubmit?: (fv: FieldValues) => void;
  onChange?: (fv: FieldValues, event: { name?: string; type?: string }) => void;
  onPinnedFiltersChange?: () => void;
  panelWidth?: number | string;
  tid?: string;
  paperTid?: string;
  panelTid?: string;
};

type FilterFieldMetadata = {
  isEmpty?: (value: unknown) => boolean;
  fromUrl?: (value: unknown) => unknown;
  toUrl?: (value: unknown) => unknown;
};

type FilterMetadata = {
  keys: string[];
  fields: Record<string, FilterFieldMetadata>;
};

const DEFAULT_RANGE_MIN = 0;
const DEFAULT_RANGE_MAX = 100;

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string' && value.trim()) {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  return undefined;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

const isRangeValue = (value: unknown): value is { min?: unknown; max?: unknown } => {
  return isPlainObject(value) && ('min' in value || 'max' in value);
};

const isCalendarValue = (value: unknown): value is Record<string, unknown> => {
  return (
    isPlainObject(value) && ('startDate' in value || 'endDate' in value || 'start' in value || 'end' in value)
  );
};

const isRangeEmpty = (value: unknown, bounds: { min: number; max: number }): boolean => {
  if (!isRangeValue(value)) return true;

  const min = toFiniteNumber(value.min);
  const max = toFiniteNumber(value.max);
  const isMinEmpty = min === undefined || min === bounds.min;
  const isMaxEmpty = max === undefined || max === bounds.max;

  return isMinEmpty && isMaxEmpty;
};

const getChildDisplayName = (child: ReactNode): string | undefined => {
  if (!isValidElement(child)) return undefined;

  const childType = child.type as { displayName?: unknown };

  return typeof childType.displayName === 'string' ? childType.displayName : undefined;
};

const getRangeBounds = (props: ChildWithFilterName): { min: number; max: number } => {
  const min = isFiniteNumber(props.min) ? props.min : DEFAULT_RANGE_MIN;
  const max = isFiniteNumber(props.max) && props.max > min ? props.max : DEFAULT_RANGE_MAX;

  return { min, max };
};

const parseUrlValue = (value: string): unknown => {
  const trimmedValue = value.trim();

  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) return value;

  try {
    const parsedValue = JSON.parse(trimmedValue);

    return typeof parsedValue === 'object' && parsedValue !== null ? parsedValue : value;
  } catch {
    return value;
  }
};

const toCalendarFormValue = (value: unknown): unknown => {
  if (!isCalendarValue(value)) return value;

  return {
    startDate: value.startDate ?? value.start ?? null,
    endDate: value.endDate ?? value.end ?? null,
  };
};

const toCalendarUrlValue = (value: unknown): unknown => {
  if (!isCalendarValue(value)) return value;

  const start = value.startDate ?? value.start;
  const end = value.endDate ?? value.end;

  return {
    ...(hasSelectionValue(start) ? { start } : {}),
    ...(hasSelectionValue(end) ? { end } : {}),
  };
};

const toRangeFormValue = (value: unknown): unknown => {
  if (!isRangeValue(value)) return value;

  return {
    ...(toFiniteNumber(value.min) !== undefined ? { min: toFiniteNumber(value.min) } : {}),
    ...(toFiniteNumber(value.max) !== undefined ? { max: toFiniteNumber(value.max) } : {}),
  };
};

const toRangeUrlValue = (value: unknown, bounds: { min: number; max: number }): unknown => {
  if (!isRangeValue(value)) return value;

  const min = toFiniteNumber(value.min);
  const max = toFiniteNumber(value.max);

  return {
    ...(min !== undefined && min !== bounds.min ? { min } : {}),
    ...(max !== undefined && max !== bounds.max ? { max } : {}),
  };
};

const toMultiboxFormValue = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.flatMap(toMultiboxFormValue);
  if (value === null || value === undefined) return [];
  if (!['string', 'number', 'boolean'].includes(typeof value)) return [];

  const optionId = String(value).trim();

  return optionId ? [optionId] : [];
};

const toListUrlValue = (value: unknown): string => {
  return JSON.stringify(toMultiboxFormValue(value));
};

const getFilterFieldMetadata = (child: ReactNode, props: ChildWithFilterName): FilterFieldMetadata => {
  const childDisplayName = getChildDisplayName(child);

  if (childDisplayName === 'SiloReal-Filter-Field-Multibox') {
    return {
      fromUrl: toMultiboxFormValue,
      toUrl: toListUrlValue,
    };
  }

  if (
    props.multiple === true &&
    (childDisplayName === 'SiloReal-Filter-Field-Selector' ||
      childDisplayName === 'SiloReal-Collection-ServiceSelector')
  ) {
    return {
      fromUrl: toMultiboxFormValue,
      toUrl: toListUrlValue,
    };
  }

  if (childDisplayName === 'SiloReal-Filter-Field-Calendar') {
    return {
      fromUrl: toCalendarFormValue,
      toUrl: toCalendarUrlValue,
    };
  }

  if (childDisplayName === 'SiloReal-Filter-Field-Range') {
    const bounds = getRangeBounds(props);

    return {
      isEmpty: (value) => isRangeEmpty(value, bounds),
      fromUrl: toRangeFormValue,
      toUrl: (value) => toRangeUrlValue(value, bounds),
    };
  }

  return {};
};

const hasSelectionValue = (value: unknown, metadata?: FilterFieldMetadata): boolean => {
  if (metadata?.isEmpty) return !metadata.isEmpty(value);
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.some((item) => hasSelectionValue(item));
  if (typeof value === 'object') return Object.values(value).some((item) => hasSelectionValue(item));
  return true;
};

const hasAnySelection = (values: FieldValues, fields: FilterMetadata['fields'] = {}): boolean => {
  return Object.entries(values).some(([key, value]) => hasSelectionValue(value, fields[key]));
};

const countSelections = (values: FieldValues, fields: FilterMetadata['fields'] = {}): number => {
  return Object.entries(values).filter(([key, value]) => hasSelectionValue(value, fields[key])).length;
};

type ChildWithFilterName = {
  name?: unknown;
  min?: unknown;
  max?: unknown;
  multiple?: unknown;
  children?: ReactNode;
};

type DrawerView = 'filters' | 'saved';

type SavedFilter = {
  id: string;
  reference: string;
  createdAt: string;
  values: FieldValues;
  pinned: boolean;
};

export type FilterPinnedFilter = {
  id: string;
  reference: string;
  createdAt: string;
  values: FieldValues;
};

const SAVE_REFERENCE_FIELD = 'reference';
const MAX_PINNED_FILTERS = 2;

const copyFieldValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(copyFieldValue);

  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, copyFieldValue(item)]));
  }

  return value;
};

const copyFieldValues = (values: FieldValues): FieldValues => {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, copyFieldValue(value)]));
};

const normalizeFilterValues = (values: FieldValues, fields: FilterMetadata['fields'] = {}): FieldValues => {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([key, value]) => hasSelectionValue(value, fields[key]))
      .map(([key, value]) => [key, copyFieldValue(value)])
  );
};

const readFilterUrlValues = (params: URLSearchParams, metadata: FilterMetadata): FieldValues => {
  return metadata.keys.reduce<FieldValues>((values, key) => {
    const urlValues = params.getAll(key);
    const fieldMetadata = metadata.fields[key];

    if (urlValues.length === 0) return values;

    const rawValue = urlValues.length === 1 ? parseUrlValue(urlValues[0]) : urlValues.map(parseUrlValue);
    const value = fieldMetadata?.fromUrl ? fieldMetadata.fromUrl(rawValue) : rawValue;

    if (!hasSelectionValue(value, fieldMetadata)) return values;

    return {
      ...values,
      [key]: value,
    };
  }, {});
};

const writeFilterUrlValues = (
  values: Partial<FieldValues>,
  metadata: FilterMetadata
): Record<string, unknown> => {
  return metadata.keys.reduce<Record<string, unknown>>((urlValues, key) => {
    const fieldMetadata = metadata.fields[key];
    const value = values[key];

    if (!hasSelectionValue(value, fieldMetadata)) return urlValues;

    const urlValue = fieldMetadata?.toUrl ? fieldMetadata.toUrl(value) : value;

    if (!hasSelectionValue(urlValue)) return urlValues;

    return {
      ...urlValues,
      [key]: urlValue,
    };
  }, {});
};

const toSavedFilterDate = (value: unknown): string => {
  const date = value ? new Date(value as string | number | Date) : new Date();

  if (Number.isNaN(date.getTime())) return new Date().toISOString();

  return date.toISOString();
};

const formatSavedFilterDate = (date: string): string => {
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(new Date(date));
};

const normalizeSavedFilters = (silobagList: unknown): SavedFilter[] => {
  if (!Array.isArray(silobagList)) return [];

  return silobagList.map((item, index) => {
    const entry = item && typeof item === 'object' ? (item as Partial<SavedFilter>) : {};
    const values = entry.values && typeof entry.values === 'object' ? entry.values : {};

    return {
      id: typeof entry.id === 'string' ? entry.id : String(index),
      reference: typeof entry.reference === 'string' ? entry.reference : '',
      createdAt: toSavedFilterDate(entry.createdAt),
      values: copyFieldValues(values),
      pinned: entry.pinned === true,
    };
  });
};

const sortSavedFilters = (filters: SavedFilter[]): SavedFilter[] => {
  return filters
    .map((savedFilter, index) => ({ savedFilter, index }))
    .sort((a, b) => {
      if (a.savedFilter.pinned !== b.savedFilter.pinned) {
        return a.savedFilter.pinned ? -1 : 1;
      }

      return a.index - b.index;
    })
    .map(({ savedFilter }) => savedFilter);
};

const toPinnedFilter = (savedFilter: SavedFilter): FilterPinnedFilter => ({
  id: savedFilter.id,
  reference: savedFilter.reference,
  createdAt: savedFilter.createdAt,
  values: copyFieldValues(savedFilter.values),
});

const getFilterMetadata = (children: ReactNode): FilterMetadata => {
  const keys = new Set<string>();
  const fields: FilterMetadata['fields'] = {};

  const collect = (node: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement<ChildWithFilterName>(child)) return;

      collect(child.props.children);

      const fieldName =
        typeof child.props.name === 'string'
          ? child.props.name
          : typeof child.key === 'string' && child.key.length > 0 && !child.key.startsWith('.')
            ? child.key
            : undefined;

      if (fieldName && !fields[fieldName]) {
        keys.add(fieldName);
        fields[fieldName] = getFilterFieldMetadata(child, child.props);
      }
    });
  };

  collect(children);

  return {
    keys: Array.from(keys),
    fields,
  };
};

export const base: FR<FilterBaseHandle, Props> = forwardRef((props, ref) => {
  const {
    children,
    drawer = false,
    open,
    onClose,
    onSubmit,
    onChange,
    onPinnedFiltersChange,
    panelWidth = 420,
    tid,
    paperTid,
    panelTid,
  } = props;

  const [discardVersion, setDiscardVersion] = useState(0);
  const [hasDraftFilters, setHasDraftFilters] = useState(false);
  const [hasAppliedFilterValues, setHasAppliedFilterValues] = useState(false);
  const [defaultFilterValues, setDefaultFilterValues] = useState<FieldValues | undefined>();
  const [drawerView, setDrawerView] = useState<DrawerView>('filters');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [editingSavedFilterId, setEditingSavedFilterId] = useState<string | undefined>();
  const [renamingSavedFilterId, setRenamingSavedFilterId] = useState<string | undefined>();
  const [renamingSavedFilterReference, setRenamingSavedFilterReference] = useState('');
  const storedSavedFilters = useGlobalStore((state) => state.filter_silobag_list);
  const setStoredSavedFilters = useGlobalStore((state) => state.filter_set_silobag_list);
  const activeFilterValuesRef = useRef<FieldValues>({});
  const draftFilterValuesRef = useRef<FieldValues>({});
  const didApplyInitialUrlValues = useRef(false);
  const filterMetadata = useMemo(() => getFilterMetadata(children), [children]);
  const filterKeys = filterMetadata.keys;
  const filterFields = filterMetadata.fields;
  const readUrlFilters = useCallback(
    (params: URLSearchParams): Partial<FieldValues> => readFilterUrlValues(params, filterMetadata),
    [filterMetadata]
  );
  const writeUrlFilters = useCallback(
    (values: Partial<FieldValues>): Record<string, unknown> => writeFilterUrlValues(values, filterMetadata),
    [filterMetadata]
  );
  const { query: filterQuery } = useRouteController<FieldValues, object>({
    options: {
      query: {
        scope: filterKeys,
        parse: readUrlFilters,
        format: writeUrlFilters,
        history: drawer ? 'push' : 'replace',
      },
    },
  });
  const initialFilterValues = useMemo(
    () => defaultFilterValues ?? filterQuery.current,
    [defaultFilterValues, discardVersion, filterQuery]
  );
  const savedFilters = useMemo(() => normalizeSavedFilters(storedSavedFilters), [storedSavedFilters]);
  const sortedSavedFilters = useMemo(() => sortSavedFilters(savedFilters), [savedFilters]);
  const pinnedSavedFilters = useMemo(
    () => sortedSavedFilters.filter((savedFilter) => savedFilter.pinned),
    [sortedSavedFilters]
  );
  const unpinnedSavedFilters = useMemo(
    () => sortedSavedFilters.filter((savedFilter) => !savedFilter.pinned),
    [sortedSavedFilters]
  );
  const pinnedSavedFilterCount = useMemo(
    () => savedFilters.filter((savedFilter) => savedFilter.pinned).length,
    [savedFilters]
  );
  const editingSavedFilter = useMemo(
    () => savedFilters.find((savedFilter) => savedFilter.id === editingSavedFilterId),
    [editingSavedFilterId, savedFilters]
  );

  const setSavedFilters = useCallback(
    (updater: (current: SavedFilter[]) => SavedFilter[]): void => {
      setStoredSavedFilters(updater(savedFilters));
    },
    [savedFilters, setStoredSavedFilters]
  );

  const setActiveValues = useCallback(
    (values: FieldValues): FieldValues => {
      const activeValues = normalizeFilterValues(values, filterFields);

      activeFilterValuesRef.current = activeValues;
      setHasAppliedFilterValues(hasAnySelection(activeValues, filterFields));

      return activeValues;
    },
    [filterFields]
  );

  const clearActiveFilters = useCallback((): void => {
    const emptyValues: FieldValues = {};

    filterQuery.clear();
    draftFilterValuesRef.current = emptyValues;
    setActiveValues(emptyValues);
    setDefaultFilterValues(emptyValues);
    setHasDraftFilters(false);
    setEditingSavedFilterId(undefined);
    setDrawerView('filters');
    setDiscardVersion((current) => current + 1);

    if (drawer) {
      onSubmit?.(emptyValues);
      return;
    }

    onChange?.(emptyValues, { type: 'clear' });
  }, [drawer, filterQuery, onChange, onSubmit, setActiveValues]);

  useEffect(() => {
    if (didApplyInitialUrlValues.current) return;
    didApplyInitialUrlValues.current = true;

    const initialActiveValues = normalizeFilterValues(initialFilterValues, filterFields);

    if (!hasAnySelection(initialActiveValues, filterFields)) return;

    setHasDraftFilters(true);
    draftFilterValuesRef.current = initialActiveValues;
    setActiveValues(initialActiveValues);
    filterQuery.change(initialActiveValues);

    if (drawer) {
      onSubmit?.(initialActiveValues);
      return;
    }

    onChange?.(initialActiveValues, { type: 'init' });
  }, [drawer, filterFields, initialFilterValues, onChange, onSubmit, setActiveValues]);

  const toActionTid = (suffix: string): string | undefined => {
    if (tid) return `${tid}-${suffix}`;
    if (panelTid) return `${panelTid}-${suffix}`;
    return undefined;
  };

  const handleDiscardAndClose = (): void => {
    const activeValues = activeFilterValuesRef.current;

    draftFilterValuesRef.current = activeValues;
    setDefaultFilterValues(activeValues);
    setDiscardVersion((current) => current + 1);
    setHasDraftFilters(hasAnySelection(activeValues, filterFields));
    setDrawerView('filters');
    setEditingSavedFilterId(undefined);
    onClose?.();
  };

  const handleClearSelection = (): void => {
    draftFilterValuesRef.current = {};
    setDefaultFilterValues({});
    setDiscardVersion((current) => current + 1);
    setHasDraftFilters(false);
  };

  const canSaveFilters = hasDraftFilters || hasAppliedFilterValues;

  const getValuesToSave = (): FieldValues => {
    if (editingSavedFilterId) return draftFilterValuesRef.current;

    return hasAnySelection(draftFilterValuesRef.current, filterFields)
      ? draftFilterValuesRef.current
      : activeFilterValuesRef.current;
  };

  const handleSave = (): void => {
    if (!canSaveFilters) return;

    if (editingSavedFilterId) {
      const valuesToSave = getValuesToSave();

      if (!hasAnySelection(valuesToSave, filterFields)) return;

      setSavedFilters((current) =>
        current.map((savedFilter) =>
          savedFilter.id === editingSavedFilterId
            ? {
                ...savedFilter,
                values: copyFieldValues(valuesToSave),
              }
            : savedFilter
        )
      );
      setEditingSavedFilterId(undefined);
      setDrawerView('saved');
      return;
    }

    setIsSaveModalOpen(true);
  };

  const handleCloseSaveModal = (): void => {
    setIsSaveModalOpen(false);
  };

  const handleSaveFilter = (fv: FieldValues): void => {
    const reference = String(fv[SAVE_REFERENCE_FIELD] ?? '').trim();

    if (!reference) return;

    const valuesToSave = getValuesToSave();

    if (!hasAnySelection(valuesToSave, filterFields)) return;

    setSavedFilters((current) => [
      {
        id: `${Date.now()}-${current.length}`,
        reference,
        createdAt: new Date().toISOString(),
        values: copyFieldValues(valuesToSave),
        pinned: false,
      },
      ...current,
    ]);
    setIsSaveModalOpen(false);
  };

  const handleShowSavedFilters = (): void => {
    setEditingSavedFilterId(undefined);
    setDrawerView('saved');
  };

  const handleBackToFilters = (): void => {
    setDrawerView('filters');
  };

  const handleDeleteSavedFilter = (id: string): void => {
    setSavedFilters((current) => current.filter((savedFilter) => savedFilter.id !== id));

    if (editingSavedFilterId === id) {
      setEditingSavedFilterId(undefined);
    }

    if (renamingSavedFilterId === id) {
      setRenamingSavedFilterId(undefined);
      setRenamingSavedFilterReference('');
    }
  };

  const handleCancelRenameSavedFilter = (): void => {
    setRenamingSavedFilterId(undefined);
    setRenamingSavedFilterReference('');
  };

  const handleStartRenameSavedFilter = (savedFilter: SavedFilter): void => {
    setRenamingSavedFilterId(savedFilter.id);
    setRenamingSavedFilterReference(savedFilter.reference);
  };

  const handleConfirmRenameSavedFilter = (): void => {
    if (!renamingSavedFilterId) return;

    const reference = renamingSavedFilterReference.trim();

    if (!reference) return;

    setSavedFilters((current) =>
      current.map((savedFilter) =>
        savedFilter.id === renamingSavedFilterId
          ? {
              ...savedFilter,
              reference,
            }
          : savedFilter
      )
    );
    handleCancelRenameSavedFilter();
  };

  const handleRenameSavedFilterReferenceChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setRenamingSavedFilterReference(event.target.value);
  };

  const handleRenameSavedFilterBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    handleCancelRenameSavedFilter();
  };

  const handleRenameSavedFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirmRenameSavedFilter();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancelRenameSavedFilter();
    }
  };

  const handleEditSavedFilter = (savedFilter: SavedFilter): void => {
    const values = copyFieldValues(savedFilter.values);

    handleCancelRenameSavedFilter();
    setEditingSavedFilterId(savedFilter.id);
    draftFilterValuesRef.current = values;
    setDefaultFilterValues(values);
    setDiscardVersion((current) => current + 1);
    setHasDraftFilters(hasAnySelection(values, filterFields));
    setDrawerView('filters');
  };

  const handlePinSavedFilter = (savedFilter: SavedFilter): void => {
    setSavedFilters((current) => {
      const pinnedCount = current.filter((currentSavedFilter) => currentSavedFilter.pinned).length;

      return current.map((currentSavedFilter) => {
        if (currentSavedFilter.id !== savedFilter.id) return currentSavedFilter;

        if (!currentSavedFilter.pinned && pinnedCount >= MAX_PINNED_FILTERS) return currentSavedFilter;

        return {
          ...currentSavedFilter,
          pinned: !currentSavedFilter.pinned,
        };
      });
    });
  };

  const handleApplySavedFilter = (savedFilter: SavedFilter): void => {
    const values = copyFieldValues(savedFilter.values);

    handleCancelRenameSavedFilter();
    const activeValues = setActiveValues(values);

    draftFilterValuesRef.current = activeValues;
    filterQuery.change(activeValues);
    setDefaultFilterValues(activeValues);
    setHasDraftFilters(hasAnySelection(activeValues, filterFields));
    setDiscardVersion((current) => current + 1);
    setDrawerView('filters');
    setEditingSavedFilterId(undefined);
    onSubmit?.(activeValues);
    onClose?.();
  };

  const handleApplyPinnedFilter = (id: string): void => {
    const pinnedFilter = pinnedSavedFilters.find((savedFilter) => savedFilter.id === id);

    if (!pinnedFilter) return;

    handleApplySavedFilter(pinnedFilter);
  };

  useImperativeHandle(
    ref,
    () => ({
      read: () => activeFilterValuesRef.current,
      count: () => countSelections(activeFilterValuesRef.current, filterFields),
      hasActiveFilters: () => hasAnySelection(activeFilterValuesRef.current, filterFields),
      pinned: () => pinnedSavedFilters.map(toPinnedFilter),
      applyPinned: handleApplyPinnedFilter,
      clear: clearActiveFilters,
      reset: clearActiveFilters,
    }),
    [clearActiveFilters, filterFields, handleApplyPinnedFilter, pinnedSavedFilters]
  );

  useEffect(() => {
    onPinnedFiltersChange?.();
  }, [onPinnedFiltersChange, pinnedSavedFilters]);

  const canApplyFilters = hasDraftFilters || hasAppliedFilterValues;

  const handleInternalSubmit = (fv: FieldValues): void => {
    const activeValues = normalizeFilterValues(fv, filterFields);

    if (drawer) {
      setEditingSavedFilterId(undefined);
      draftFilterValuesRef.current = activeValues;

      if (hasAnySelection(activeValues, filterFields)) {
        filterQuery.change(activeValues);
      } else {
        filterQuery.clear();
      }

      setActiveValues(activeValues);
      setDefaultFilterValues(activeValues);
    }

    onSubmit?.(activeValues);
  };

  const handleInternalChange = (fv: FieldValues, event: { name?: string; type?: string }): void => {
    const draftValues = normalizeFilterValues(fv, filterFields);

    draftFilterValuesRef.current = draftValues;
    setHasDraftFilters(hasAnySelection(draftValues, filterFields));

    if (!drawer) {
      if (hasAnySelection(draftValues, filterFields)) {
        filterQuery.change(draftValues);
      } else {
        filterQuery.clear();
      }

      setActiveValues(draftValues);
    }

    onChange?.(draftValues, event);
  };

  const i18n = HOOKS.useLocale();

  const filterDrawerContent = (
    <>
      {drawer && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexShrink={0}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="title" level="h6">
                {i18n.t('temp.common.filters')}
              </Typography>

              <Button
                variant="alternative-hollow"
                removeBorder
                type="button"
                icon={<BookmarkBorderOutlinedIcon fontSize="small" />}
                tid={toActionTid('saved-filters-btn')}
                onClick={handleShowSavedFilters}
              >
                {`${i18n.t('temp.common.your_filters')}${savedFilters.length > 0 ? ` (${savedFilters.length})` : ''}`}
              </Button>
            </Stack>

            <Button
              variant="outlined"
              type="button"
              icon={{ value: <CloseIcon fontSize="small" />, only: true }}
              ariaLabel="Cerrar filtros"
              tid={toActionTid('close-btn')}
              onClick={handleDiscardAndClose}
            />
          </Stack>

          <FormDivider size="full" tid={toActionTid('header-divider')} />
        </>
      )}

      {drawer ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            pr: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Stack gap={2} sx={{ width: '100%', maxWidth: '100%' }} pt={0.5}>
            {children}
          </Stack>
        </Box>
      ) : (
        children
      )}

      {drawer && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} flexShrink={0}>
          <MuiButton
            type="button"
            color="error"
            disabled={!hasDraftFilters}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={handleClearSelection}
            data-testid={toActionTid('discard-btn')}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {i18n.t('temp.common.clear_selection')}
          </MuiButton>

          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            {editingSavedFilter && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '39%',
                  bottom: 'calc(100% - 4px)',
                  transform: 'translateX(-50%)',
                  maxWidth: 144,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: '#22A7F0',
                  color: 'common.white',
                  boxShadow: 2,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  pointerEvents: 'none',
                  zIndex: 1,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    bottom: -6,
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid',
                    borderTopColor: '#22A7F0',
                  },
                }}
                title={editingSavedFilter.reference}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {editingSavedFilter.reference}
                </Box>
              </Box>
            )}

            <Button
              variant="alternative-hollow"
              removeBorder
              type="button"
              onClick={handleSave}
              disabled={!canSaveFilters}
              icon={<BookmarkAddOutlinedIcon fontSize="small" />}
              tid={toActionTid('save-btn')}
            >
              {i18n.t('temp.common.save')}
            </Button>
          </Box>

          <Button
            variant="primary"
            type="submit"
            disabled={!canApplyFilters}
            elevation={false}
            uppercase={false}
            icon={<CheckIcon fontSize="small" />}
            tid={toActionTid('apply-btn')}
          >
            {i18n.t('temp.common.apply')}
          </Button>
        </Stack>
      )}
    </>
  );

  const renderSavedFilterItem = (savedFilter: SavedFilter): ReactNode => {
    const isPinDisabled = !savedFilter.pinned && pinnedSavedFilterCount >= MAX_PINNED_FILTERS;
    const isPinned = savedFilter.pinned;
    const isRenaming = renamingSavedFilterId === savedFilter.id;

    return (
      <Box
        key={savedFilter.id}
        data-testid={toActionTid(`saved-filter-${savedFilter.id}`)}
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          borderRadius: 2,
          border: '1px solid',
          borderColor: isPinned ? 'primary.main' : 'transparent',
          backgroundColor: isPinned ? '#6BAA0014' : 'transparent',
          boxShadow: isPinned ? 1 : 0,
          px: isPinned ? 1.5 : 0,
          py: isPinned ? 1 : 0,
          transition: 'background-color 160ms ease, border-color 160ms ease',
        }}
      >
        <Stack gap={0.75} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ width: '100%', minWidth: 0 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isRenaming ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  onBlur={handleRenameSavedFilterBlur}
                  sx={{ minWidth: 0, maxWidth: '100%' }}
                >
                  <Box
                    component="input"
                    autoFocus
                    value={renamingSavedFilterReference}
                    aria-label={`Editar nombre del filtro ${savedFilter.reference}`}
                    onChange={handleRenameSavedFilterReferenceChange}
                    onKeyDown={handleRenameSavedFilterKeyDown}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 32,
                      px: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      outline: 'none',
                      fontSize: 14,
                      fontWeight: 700,
                      '&:focus': {
                        borderColor: '#22A7F0',
                        boxShadow: '0 0 0 2px rgba(34, 167, 240, 0.16)',
                      },
                    }}
                  />

                  <Button
                    variant="alternative"
                    type="button"
                    icon={{ value: <CheckIcon fontSize="small" />, only: true }}
                    disabled={!renamingSavedFilterReference.trim()}
                    ariaLabel={`Confirmar nombre del filtro ${savedFilter.reference}`}
                    tid={toActionTid(`saved-filter-${savedFilter.id}-rename-confirm-btn`)}
                    onClick={handleConfirmRenameSavedFilter}
                  />

                  <Button
                    variant="danger"
                    type="button"
                    icon={{ value: <CloseIcon fontSize="small" />, only: true }}
                    ariaLabel={`Cancelar edición del nombre ${savedFilter.reference}`}
                    tid={toActionTid(`saved-filter-${savedFilter.id}-rename-cancel-btn`)}
                    onClick={handleCancelRenameSavedFilter}
                  />
                </Stack>
              ) : (
                <Box
                  component="button"
                  type="button"
                  title={savedFilter.reference}
                  onClick={() => handleStartRenameSavedFilter(savedFilter)}
                  sx={{
                    display: 'inline-block',
                    width: 'fit-content',
                    maxWidth: '100%',
                    minWidth: 0,
                    p: 0,
                    border: 0,
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.35,
                    '&:hover': {
                      color: '#22A7F0',
                    },
                  }}
                >
                  {savedFilter.reference}
                </Box>
              )}
            </Box>

            <Button
              variant={savedFilter.pinned ? 'primary' : 'alternative'}
              type="button"
              icon={{ value: <PushPinOutlinedIcon fontSize="small" />, only: true }}
              disabled={isPinDisabled}
              ariaLabel={
                savedFilter.pinned
                  ? `Quitar pin del filtro ${savedFilter.reference}`
                  : `Fijar filtro ${savedFilter.reference}`
              }
              tid={toActionTid(`saved-filter-${savedFilter.id}-pin-btn`)}
              onClick={() => handlePinSavedFilter(savedFilter)}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
          >
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
              <Typography variant="secondary">{formatSavedFilterDate(savedFilter.createdAt)}</Typography>

              <Button
                variant="danger"
                type="button"
                icon={{ value: <DeleteOutlineIcon fontSize="small" />, only: true }}
                ariaLabel={`Eliminar filtro ${savedFilter.reference}`}
                tid={toActionTid(`saved-filter-${savedFilter.id}-delete-btn`)}
                onClick={() => handleDeleteSavedFilter(savedFilter.id)}
              />

              <Button
                variant="alternative"
                type="button"
                icon={{ value: <EditOutlinedIcon fontSize="small" />, only: true }}
                ariaLabel={`Editar filtro ${savedFilter.reference}`}
                tid={toActionTid(`saved-filter-${savedFilter.id}-edit-btn`)}
                onClick={() => handleEditSavedFilter(savedFilter)}
              />
            </Stack>

            <Button
              variant="text-link"
              type="button"
              icon={<CheckIcon fontSize="small" />}
              tid={toActionTid(`saved-filter-${savedFilter.id}-apply-btn`)}
              onClick={() => handleApplySavedFilter(savedFilter)}
            >
              {i18n.t('temp.common.apply_filter')}
            </Button>
          </Stack>
        </Stack>

        {!isPinned && (
          <Box mt={1.25}>
            <FormDivider size="full" tid={toActionTid(`saved-filter-${savedFilter.id}-divider`)} />
          </Box>
        )}
      </Box>
    );
  };

  const savedFiltersContent = (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        boxSizing: 'border-box',
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
        <Button
          variant="outlined"
          type="button"
          icon={{ value: <ArrowBackIcon fontSize="small" />, only: true }}
          ariaLabel="Volver a filtros"
          tid={toActionTid('back-to-filters-btn')}
          onClick={handleBackToFilters}
        />

        <Typography variant="title" level="h6">
          {i18n.t('temp.common.your_filters')}
        </Typography>
      </Stack>

      {pinnedSavedFilters.length > 0 && (
        <Stack gap={1} sx={{ width: '100%', maxWidth: '100%' }} flexShrink={0}>
          {pinnedSavedFilters.map(renderSavedFilterItem)}
        </Stack>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          mr: -2,
          pr: 2,
        }}
      >
        <Stack gap={1} sx={{ width: '100%', maxWidth: '100%' }}>
          {sortedSavedFilters.length === 0 && (
            <Typography variant="secondary">{i18n.t('temp.common.no_filters_yet')}</Typography>
          )}

          {unpinnedSavedFilters.map(renderSavedFilterItem)}
        </Stack>
      </Box>
    </Box>
  );

  const panel = (
    <Box
      data-testid={drawer ? panelTid : (panelTid ?? tid)}
      sx={{
        width: panelWidth,
        maxWidth: '100%',
        height: drawer ? '100%' : 'auto',
        overflowX: 'hidden',
        '& > form': {
          maxWidth: '100%',
          overflowX: 'hidden',
          ...(drawer
            ? {
                height: '100%',
                minHeight: 0,
                overflowY: 'hidden',
              }
            : {}),
        },
      }}
    >
      <FormBase
        key={discardVersion}
        defaultValues={initialFilterValues}
        onSubmit={handleInternalSubmit}
        onChange={handleInternalChange}
      >
        {drawer && drawerView === 'saved' ? savedFiltersContent : filterDrawerContent}
      </FormBase>

      {drawer && (
        <Modal
          open={isSaveModalOpen}
          onClose={handleCloseSaveModal}
          showCloseButton={false}
          tid={toActionTid('save-modal')}
          contentTid={toActionTid('save-modal-content')}
        >
          <Box
            sx={{
              width: { xs: 'calc(100vw - 64px)', sm: 480 },
              maxWidth: '100%',
              overflow: 'hidden',
              '& .MuiTextField-root': {
                width: '100%',
                minWidth: 0,
              },
            }}
          >
            <FormBase key={String(isSaveModalOpen)} onSubmit={handleSaveFilter}>
              <Typography variant="title" level="h6">
                {i18n.t('temp.common.save_filters_configuration')}
              </Typography>

              <TextInput
                name={SAVE_REFERENCE_FIELD}
                tid={toActionTid('save-reference-field') ?? 'filter-save-reference-field'}
                label={i18n.t('temp.common.reference')}
                required
              />

              <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2} flexWrap="wrap">
                <Button
                  variant="text-link"
                  type="button"
                  onClick={handleCloseSaveModal}
                  tid={toActionTid('save-cancel-btn')}
                >
                  {i18n.t('temp.common.cancel')}
                </Button>

                <Button
                  variant="primary"
                  type="submit"
                  uppercase={false}
                  elevation={false}
                  tid={toActionTid('save-confirm-btn')}
                >
                  {i18n.t('temp.common.save_filter')}
                </Button>
              </Stack>
            </FormBase>
          </Box>
        </Modal>
      )}
    </Box>
  );

  if (!drawer) {
    return panel;
  }

  return (
    <Drawer
      open={Boolean(open)}
      onClose={handleDiscardAndClose}
      position="left"
      tid={tid}
      paperTid={paperTid}
    >
      {panel}
    </Drawer>
  );
});

base.displayName = 'SiloReal-Filter';
